import React, { useState, useEffect } from 'react';
import { Cpu, Zap, Activity, HardDrive, Thermometer, Settings } from 'lucide-react';

const GPUInfo = () => {
  const [gpuInfo, setGpuInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [llmConfig, setLlmConfig] = useState({
    gpu_enabled: true,
    gpu_layers: -1,  // -1 = all layers
    main_gpu: 0,
    tensor_split: '',
    low_vram: false,
    numa: false,
    flash_attention: true,
    batch_size: 512,
    context_length: 4096,
    threads: 8
  });

  useEffect(() => {
    let isMounted = true;
    let intervalId = null;

    // Récupérer infos GPU depuis backend
    const fetchGPU = async () => {
      try {
        // Utiliser le proxy du frontend (port 3000) pour éviter les problèmes CORS/réseau
        // Le proxy redirige /api/* vers le backend 5001
        const apiUrl = '/api/v1/system/gpu';

        const token = localStorage.getItem('access_token');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Vérifier si le composant est toujours monté
        if (!isMounted) return;

        if (response.ok) {
          // Vérifier le content-type avant de parser JSON
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            if (isMounted) {
              setGpuInfo(data);
              setError(null);
            }
          } else {
            // Réponse non-JSON (probablement erreur proxy)
            if (isMounted) {
              setError('Réponse invalide du serveur');
            }
          }
        } else {
          if (isMounted) {
            setError(`HTTP ${response.status}`);
          }
        }
      } catch (err) {
        if (isMounted) {
          // Gérer spécifiquement l'erreur Response.clone
          if (err.message && err.message.includes('clone')) {
            setError('Erreur de connexion temporaire');
          } else {
            setError(err.message || 'Erreur de connexion');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchGPU();
    intervalId = setInterval(fetchGPU, 10000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const ResourceBar = ({ label, value, max, unit, color = 'brand-green' }) => {
    const percent = max > 0 ? (value / max) * 100 : 0;
    
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">{label}</span>
          <span className="text-sm font-bold text-brand-green">
            {value}{unit} / {max}{unit}
          </span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-2">
          <div
            className={`h-full rounded-full bg-${color}`}
            style={{ width: `${percent}%` }}
          ></div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-brand-black rounded-lg p-6 border border-brand-green/30">
        <div className="flex items-center gap-3">
          <div className="animate-spin h-6 w-6 border-2 border-brand-green border-t-transparent rounded-full"></div>
          <div className="text-gray-400">Chargement des informations GPU...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <div className="text-red-500 text-2xl">❌</div>
          <div>
            <div className="font-bold text-red-400 mb-2">Erreur de chargement GPU</div>
            <div className="text-sm text-gray-400">
              <div>Impossible de récupérer les informations GPU depuis le backend.</div>
              <div className="mt-2 text-xs text-red-300">Erreur: {error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No GPU detected
  if (!gpuInfo || !gpuInfo.available) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <div className="text-yellow-500 text-2xl">⚠️</div>
          <div>
            <div className="font-bold text-yellow-400 mb-2">Carte graphique NVIDIA non détectée</div>
            <div className="text-sm text-gray-400 space-y-1">
              <div>Aucun GPU NVIDIA n'a été détecté sur ce système.</div>
              <div>Les modèles LLM fonctionneront en mode CPU (plus lent).</div>
              <div className="mt-3 text-xs">
                Pour activer GPU:
                <ul className="list-disc ml-5 mt-1">
                  <li>Installer drivers NVIDIA</li>
                  <li>Installer CUDA Toolkit</li>
                  <li>Redémarrer le moteur SYLink AI</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Informations GPU */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Cpu className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Carte graphique NVIDIA</h3>
          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
            Actif
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Carte graphique:</span>
            <div className="text-white font-semibold">{gpuInfo.name}</div>
          </div>
          <div>
            <span className="text-gray-400">Driver NVIDIA:</span>
            <div className="text-white">{gpuInfo.driver_version}</div>
          </div>
          <div>
            <span className="text-gray-400">CUDA Version:</span>
            <div className="text-white">{gpuInfo.cuda_version}</div>
          </div>
          <div>
            <span className="text-gray-400">Compute Capability:</span>
            <div className="text-white">8.6</div>
          </div>
        </div>
      </div>

      {/* Métriques Temps Réel */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Métriques GPU (Temps Réel)</h3>
        </div>
        
        <div className="space-y-4">
          {/* Utilisation */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Utilisation GPU</span>
              <span className="text-sm font-bold text-brand-green">{gpuInfo.utilization}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-3">
              <div
                className={`h-full rounded-full transition-all ${
                  gpuInfo.utilization > 80 ? 'bg-red-500' : 
                  gpuInfo.utilization > 50 ? 'bg-yellow-500' : 'bg-brand-green'
                }`}
                style={{ width: `${gpuInfo.utilization}%` }}
              ></div>
            </div>
          </div>

          {/* VRAM */}
          <ResourceBar
            label="VRAM"
            value={gpuInfo.memory_used}
            max={gpuInfo.memory_total}
            unit="GB"
          />

          {/* Température */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400 flex items-center gap-2">
                <Thermometer className="w-4 h-4" />
                Température
              </span>
              <span className={`text-sm font-bold ${
                gpuInfo.temperature > 80 ? 'text-red-500' :
                gpuInfo.temperature > 70 ? 'text-yellow-500' : 'text-brand-green'
              }`}>
                {gpuInfo.temperature}°C
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className={`h-full rounded-full ${
                  gpuInfo.temperature > 80 ? 'bg-red-500' :
                  gpuInfo.temperature > 70 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${(gpuInfo.temperature / 100) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Puissance */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Consommation
              </span>
              <span className="text-sm font-bold text-brand-green">
                {gpuInfo.power_draw}W / {gpuInfo.power_limit}W
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className="h-full rounded-full bg-brand-green"
                style={{ width: `${(gpuInfo.power_draw / gpuInfo.power_limit) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration LLM */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-5 h-5 text-brand-green" />
          <h3 className="font-bold text-white">Configuration LLM GPU</h3>
        </div>

        <div className="space-y-4">
          {/* GPU Enabled */}
          <div className="flex items-center justify-between p-3 bg-brand-black-lighter rounded">
            <div>
              <div className="font-medium text-white">Accélération GPU</div>
              <div className="text-xs text-gray-400">Utiliser GPU pour inférence LLM (10-100x plus rapide)</div>
            </div>
            <button
              onClick={() => setLlmConfig({...llmConfig, gpu_enabled: !llmConfig.gpu_enabled})}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                llmConfig.gpu_enabled ? 'bg-brand-green' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  llmConfig.gpu_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {llmConfig.gpu_enabled && (
            <>
              {/* GPU Layers */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Nombre de Layers sur GPU
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="-1"
                    max="100"
                    value={llmConfig.gpu_layers}
                    onChange={(e) => setLlmConfig({...llmConfig, gpu_layers: parseInt(e.target.value)})}
                    className="flex-1"
                  />
                  <span className="text-brand-green font-mono w-16 text-right">
                    {llmConfig.gpu_layers === -1 ? 'ALL' : llmConfig.gpu_layers}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  -1 = Tous les layers sur GPU (recommandé). 
                  Réduire si VRAM insuffisante.
                </p>
              </div>

              {/* Context Length */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Taille du Contexte
                </label>
                <select
                  value={llmConfig.context_length}
                  onChange={(e) => setLlmConfig({...llmConfig, context_length: parseInt(e.target.value)})}
                  className="input-field w-full"
                >
                  <option value="2048">2048 tokens (rapide, VRAM faible)</option>
                  <option value="4096">4096 tokens (standard)</option>
                  <option value="8192">8192 tokens (analyse approfondie)</option>
                  <option value="16384">16384 tokens (documents longs)</option>
                  <option value="32768">32768 tokens (maximum)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Plus grand = meilleure compréhension mais plus de VRAM nécessaire
                </p>
              </div>

              {/* Batch Size */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Batch Size
                </label>
                <input
                  type="number"
                  min="128"
                  max="2048"
                  step="128"
                  value={llmConfig.batch_size}
                  onChange={(e) => setLlmConfig({...llmConfig, batch_size: parseInt(e.target.value)})}
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Plus grand = plus rapide mais plus de VRAM. 512 recommandé.
                </p>
              </div>

              {/* Flash Attention */}
              <div className="flex items-center justify-between p-3 bg-brand-black-lighter rounded">
                <div>
                  <div className="font-medium text-white">Flash Attention 2</div>
                  <div className="text-xs text-gray-400">Optimisation mémoire GPU (2-4x plus rapide)</div>
                </div>
                <button
                  onClick={() => setLlmConfig({...llmConfig, flash_attention: !llmConfig.flash_attention})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                    llmConfig.flash_attention ? 'bg-brand-green' : 'bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    llmConfig.flash_attention ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Low VRAM Mode */}
              <div className="flex items-center justify-between p-3 bg-brand-black-lighter rounded">
                <div>
                  <div className="font-medium text-white">Mode Low VRAM</div>
                  <div className="text-xs text-gray-400">Réduit utilisation mémoire (plus lent mais fonctionne sur GPU 4-6GB)</div>
                </div>
                <button
                  onClick={() => setLlmConfig({...llmConfig, low_vram: !llmConfig.low_vram})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                    llmConfig.low_vram ? 'bg-brand-green' : 'bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    llmConfig.low_vram ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Recommandations par GPU */}
      <div className="bg-brand-black rounded-lg p-5 border border-brand-green/30">
        <h3 className="font-bold text-white mb-4">Recommandations selon GPU</h3>
        
        <div className="space-y-3 text-sm">
          <div className="p-3 bg-brand-black-lighter rounded">
            <div className="font-medium text-brand-green mb-1">RTX 4090 (24GB VRAM)</div>
            <div className="text-gray-400">
              Modèle: SYLink AI
              <br />Config: gpu_layers=-1, context=32k, batch=2048
            </div>
          </div>

          <div className="p-3 bg-brand-black-lighter rounded">
            <div className="font-medium text-brand-green mb-1">RTX 4070 Ti (12GB VRAM)</div>
            <div className="text-gray-400">
              Modèle: SYLink AI
              <br />Config: gpu_layers=-1, context=8k, batch=512
            </div>
          </div>

          <div className="p-3 bg-brand-black-lighter rounded">
            <div className="font-medium text-brand-green mb-1">RTX 3060 (6GB VRAM)</div>
            <div className="text-gray-400">
              Modèle: SYLink AI
              <br />Config: gpu_layers=32, context=4k, batch=256, low_vram=ON
            </div>
          </div>
        </div>
      </div>

      {/* Optimisations */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="text-blue-500 mt-0.5">ℹ️</div>
          <div className="text-sm text-gray-300">
            <div className="font-medium text-blue-400 mb-1">Optimisations GPU</div>
            <ul className="space-y-1 text-xs">
              <li>• Flash Attention 2: Réduit VRAM de 30-40%</li>
              <li>• Quantization Q4: Divise VRAM par 2 avec perte qualité minimale</li>
              <li>• Batch size élevé: Meilleure utilisation GPU (si VRAM suffisante)</li>
              <li>• Context length: Impact majeur sur VRAM (8k = 4GB, 32k = 12GB)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GPUInfo;
